import { useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminProvider from '../providers/AdminProvider'; // Import the new AdminProvider
import { useTranslation } from 'react-i18next';
import './AuthContainer.css'; // Assuming you have an AuthContainer.css for styling

const AuthContainer = () => {
    const { t, i18n } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();

    // Menu state
    const [activeTab, setActiveTab] = useState('coupons');

    // Retrieve admin token directly from localStorage
    const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));

    // Data for display and coupon creation
    const [games, setGames] = useState([]);
    const [users, setUsers] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [surveysOffset, setSurveysOffset] = useState(0);
    const [hasMoreSurveys, setHasMoreSurveys] = useState(true);

    // Play History states
    const [playsHistory, setPlaysHistory] = useState([]);
    const [playsOffset, setPlaysOffset] = useState(0);
    const [hasMorePlays, setHasMorePlays] = useState(true);

    // Coupon Creation Form State
    const [selectedGame, setSelectedGame] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [couponMessage, setCouponMessage] = useState({ type: '', text: '' });

    // Discount Code Form State
    const [partners, setPartners] = useState([]);
    const [discountCodes, setDiscountCodes] = useState([]);
    const [dcCode, setDcCode] = useState('');
    const [dcPartnerId, setDcPartnerId] = useState('');
    const [dcGameId, setDcGameId] = useState('');
    const [dcReductionPercent, setDcReductionPercent] = useState('');
    const [dcPartnerPercent, setDcPartnerPercent] = useState('');
    const [dcValidForAll, setDcValidForAll] = useState(true);
    const [dcMessage, setDcMessage] = useState({ type: '', text: '' });

    // Partner Creation Form State
    const [pUsername, setPUsername] = useState('');
    const [pName, setPName] = useState('');
    const [pEmail, setPEmail] = useState('');
    const [pDefaultPassword, setPDefaultPassword] = useState('');
    const [pMessage, setPMessage] = useState({ type: '', text: '' });

    const [isLoading, setIsLoading] = useState(false);

    // Existing states for generic table management
    const [selectedTable, setSelectedTable] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [updateData, setUpdateData] = useState('');

    // Fetch games and users on component mount
    useEffect(() => {
        const adminProvider = new AdminProvider();
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                // Fetch all games
                const fetchedGames = await adminProvider.getAllGames(i18n.language);
                setGames(fetchedGames);
                if (fetchedGames.length > 0) {
                    setSelectedGame(fetchedGames[0].id);
                    setDcGameId(fetchedGames[0].id);
                }

                // Fetch all users
                const fetchedUsers = await adminProvider.getAllUsers();
                setUsers(fetchedUsers);

                // Fetch first 10 surveys
                const fetchedSurveys = await adminProvider.getAllSurveys(i18n.language, 10, 0);
                setSurveys(fetchedSurveys);
                setSurveysOffset(10);
                if (fetchedSurveys.length < 10) setHasMoreSurveys(false);

                // Fetch first 10 plays
                const fetchedPlays = await adminProvider.getAllPlays(10, 0);
                setPlaysHistory(fetchedPlays);
                setPlaysOffset(10);
                if (fetchedPlays.length < 10) setHasMorePlays(false);

                // Fetch partners for discount codes
                try {
                    const partnerData = await adminProvider.getPartners();
                    setPartners(partnerData.partners);
                    setDiscountCodes(partnerData.discountCodes);
                    if (partnerData.partners.length > 0) {
                        setDcPartnerId(partnerData.partners[0].id);
                    }
                } catch (e) {
                    console.error("Could not fetch partners");
                }

            } catch (error) {
                console.error('Error fetching initial admin data:', error);
                setCouponMessage({ type: 'error', text: t('admin_error_fetching_data', { defaultValue: 'Error fetching initial data for admin panel.' }) });
            } finally {
                setIsLoading(false);
            }
        };

        if (adminToken) {
            fetchInitialData();
        }
    }, [adminToken, i18n.language, t]);

    const handleCreateCoupon = async () => {
        setCouponMessage({ type: '', text: '' });
        setIsLoading(true);

        if (!selectedGame || !selectedUser) {
            setCouponMessage({ type: 'error', text: t('admin_coupon_missing_fields', { defaultValue: 'Please fill in all required coupon fields.' }) });
            setIsLoading(false);
            return;
        }

        const couponPayload = {
            gameId: selectedGame,
            userId: selectedUser
        };

        const adminProvider = new AdminProvider();
        try {
            const response = await adminProvider.createCoupon(couponPayload);
            setCouponMessage({ type: 'success', text: response.message || t('admin_coupon_created_success', { defaultValue: 'Coupon created successfully!' }) });
            setSelectedUser('');
        } catch (error) {
            console.error("Error creating coupon:", error);
            setCouponMessage({ type: 'error', text: error.message || 'Failed to create coupon.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateDiscountCode = async () => {
        setIsLoading(true);
        setDcMessage({ type: '', text: '' });
        try {
            const adminProvider = new AdminProvider();
            await adminProvider.createDiscountCode({
                code: dcCode,
                partner_id: dcPartnerId,
                reduction_percent: dcReductionPercent,
                partner_percent: dcPartnerPercent,
                valid_for_all_games: dcValidForAll,
                game_id: dcGameId
            });
            setDcMessage({ type: 'success', text: 'Discount code created successfully!' });
            setDcCode('');
            setDcReductionPercent('');
            setDcPartnerPercent('');

            const partnerData = await adminProvider.getPartners();
            setDiscountCodes(partnerData.discountCodes);
        } catch (error) {
            setDcMessage({ type: 'error', text: error.message || 'Failed to create discount code.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreatePartner = async () => {
        setIsLoading(true);
        setPMessage({ type: '', text: '' });
        try {
            const adminProvider = new AdminProvider();
            await adminProvider.createPartner({
                username: pUsername,
                name: pName,
                email: pEmail,
                defaultPassword: pDefaultPassword
            });
            setPMessage({ type: 'success', text: 'Partner created successfully!' });
            setPUsername('');
            setPName('');
            setPEmail('');
            setPDefaultPassword('');

            const partnerData = await adminProvider.getPartners();
            setPartners(partnerData.partners);
            if (partnerData.partners.length > 0) {
                setDcPartnerId(partnerData.partners[0].id);
            }
        } catch (error) {
            setPMessage({ type: 'error', text: error.message || 'Failed to create partner.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadMoreSurveys = async () => {
        setIsLoading(true);
        const adminProvider = new AdminProvider();
        try {
            const nextSurveys = await adminProvider.getAllSurveys(i18n.language, 10, surveysOffset);
            setSurveys(prev => [...prev, ...nextSurveys]);
            setSurveysOffset(prevOffset => prevOffset + 10);
            if (nextSurveys.length < 10) {
                setHasMoreSurveys(false);
            }
        } catch (error) {
            console.error('Error loading more surveys:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadMorePlays = async () => {
        setIsLoading(true);
        const adminProvider = new AdminProvider();
        try {
            const nextPlays = await adminProvider.getAllPlays(10, playsOffset);
            setPlaysHistory(prev => [...prev, ...nextPlays]);
            setPlaysOffset(prevOffset => prevOffset + 10);
            if (nextPlays.length < 10) {
                setHasMorePlays(false);
            }
        } catch (error) {
            console.error('Error loading more plays:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!adminToken) {
        return (
            <div className="admin-container unauthorized">
                <h1 className="admin-title">{t('admin_panel', { defaultValue: 'Admin Panel' })}</h1>
                <p>{t('admin_unauthorized_access', { defaultValue: 'Unauthorized access. Please log in as an administrator.' })}</p>
            </div>
        );
    }

    return (
        <div className="admin-container">
            <h1 className="admin-title">{t('admin_panel', { defaultValue: 'Admin Panel' })}</h1>

            {/* Admin Menu Navigation */}
            <div className="admin-menu">
                <button
                    className={activeTab === 'coupons' ? 'active' : 'not-active'}
                    onClick={() => setActiveTab('coupons')}
                >
                    {t('create_coupon', { defaultValue: 'Create Coupon' })}
                </button>
                <button
                    className={activeTab === 'partners' ? 'active' : 'not-active'}
                    onClick={() => setActiveTab('partners')}
                >
                    Create Partner
                </button>
                <button
                    className={activeTab === 'discountCodes' ? 'active' : 'not-active'}
                    onClick={() => setActiveTab('discountCodes')}
                >
                    Create Discount Code
                </button>
                <button
                    className={activeTab === 'surveys' ? 'active' : 'not-active'}
                    onClick={() => setActiveTab('surveys')}
                >
                    {t('all_surveys', { defaultValue: 'Surveys' })}
                </button>
                <button
                    className={activeTab === 'playHistory' ? 'active' : 'not-active'}
                    onClick={() => setActiveTab('playHistory')}
                >
                    {t('admin_play_history', { defaultValue: 'Play History' })}
                </button>
            </div>

            <div className="admin-content-area">
                {/* Coupon Creation Section */}
                {activeTab === 'coupons' && (
                    <div className="admin-section">
                        <h2 className="section-title">{t('create_coupon', { defaultValue: 'Create Coupon' })}</h2>
                        {isLoading && <p>{t('loading', { defaultValue: 'Loading' })}...</p>}
                        {couponMessage.text && (
                            <p className={`message ${couponMessage.type}`}>{couponMessage.text}</p>
                        )}
                        <div className="form-group">
                            <label htmlFor="gameSelect">{t('select_game', { defaultValue: 'Select Game' })}:</label>
                            <select
                                id="gameSelect"
                                value={selectedGame}
                                onChange={(e) => setSelectedGame(parseInt(e.target.value))}
                                disabled={isLoading}
                            >
                                {games.length === 0 && <option value="">{t('no_games_available', { defaultValue: 'No games available' })}</option>}
                                {games.map(game => (
                                    <option key={game.id} value={game.id}>{game.title} ({game.city})</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="userSelect">{t('assign_to_user', { defaultValue: 'Assign to User (Optional)' })}:</label>
                            <select
                                id="userSelect"
                                value={selectedUser || ''}
                                onChange={(e) => setSelectedUser(e.target.value ? parseInt(e.target.value) : null)}
                                disabled={isLoading}
                            >
                                <option value="">{t('none_select_user', { defaultValue: 'None (General Coupon)' })}</option>
                                {users.map(user => (
                                    <option key={user.user_id} value={user.user_id}>{user.username} ({user.email})</option>
                                ))}
                            </select>
                        </div>
                        <button onClick={handleCreateCoupon} disabled={isLoading} className="submit-button">
                            {t('create_coupon_button', { defaultValue: 'Create Coupon' })}
                        </button>
                    </div>
                )}

                {/* Partner Creation Section */}
                {activeTab === 'partners' && (
                    <div className="admin-section">
                        <h2 className="section-title">Create Partner</h2>
                        {isLoading && <p>Loading...</p>}
                        {pMessage.text && (
                            <p className={`message ${pMessage.type}`}>{pMessage.text}</p>
                        )}
                        <div className="form-group">
                            <label>Username:</label>
                            <input type="text" value={pUsername} onChange={e => setPUsername(e.target.value)} disabled={isLoading} />
                        </div>
                        <div className="form-group">
                            <label>Name (Company):</label>
                            <input type="text" value={pName} onChange={e => setPName(e.target.value)} disabled={isLoading} />
                        </div>
                        <div className="form-group">
                            <label>Email:</label>
                            <input type="email" value={pEmail} onChange={e => setPEmail(e.target.value)} disabled={isLoading} />
                        </div>
                        <div className="form-group">
                            <label>Default Password (Must change on 1st login):</label>
                            <input type="text" value={pDefaultPassword} onChange={e => setPDefaultPassword(e.target.value)} disabled={isLoading} />
                        </div>
                        <button onClick={handleCreatePartner} disabled={isLoading} className="submit-button">
                            Create Partner
                        </button>
                    </div>
                )}

                {/* Discount Code Creation Section */}
                {activeTab === 'discountCodes' && (
                    <div className="admin-section">
                        <h2 className="section-title">Create Discount Code</h2>
                        {isLoading && <p>Loading...</p>}
                        {dcMessage.text && (
                            <p className={`message ${dcMessage.type}`}>{dcMessage.text}</p>
                        )}
                        <div className="form-group">
                            <label>Code:</label>
                            <input type="text" value={dcCode} onChange={e => setDcCode(e.target.value)} disabled={isLoading} placeholder="e.g. SUMMER20" />
                        </div>
                        <div className="form-group">
                            <label>Partner:</label>
                            <select value={dcPartnerId} onChange={e => setDcPartnerId(e.target.value)} disabled={isLoading}>
                                {partners.map(p => <option key={p.id} value={p.id}>{p.name} ({p.username})</option>)}
                                {partners.length === 0 && <option value="">No partners available</option>}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Target Game (for QR & Flyer):</label>
                            <select value={dcGameId} onChange={e => setDcGameId(e.target.value)} disabled={isLoading}>
                                {games.map(g => <option key={g.id} value={g.id}>{g.title} ({g.city})</option>)}
                                {games.length === 0 && <option value="">No games available</option>}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Reduction Percent (for User):</label>
                            <input type="number" step="0.01" value={dcReductionPercent} onChange={e => setDcReductionPercent(e.target.value)} disabled={isLoading} placeholder="e.g. 10" />
                        </div>
                        <div className="form-group">
                            <label>Partner Earned Percent:</label>
                            <input type="number" step="0.01" value={dcPartnerPercent} onChange={e => setDcPartnerPercent(e.target.value)} disabled={isLoading} placeholder="e.g. 20" />
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label style={{ margin: 0 }}>Valid for all games:</label>
                            <input type="checkbox" checked={dcValidForAll} onChange={e => setDcValidForAll(e.target.checked)} disabled={isLoading} />
                        </div>
                        <button onClick={handleCreateDiscountCode} disabled={isLoading} className="submit-button">
                            Create Discount Code
                        </button>

                        <h3 style={{ marginTop: '30px' }}>Existing Discount Codes</h3>
                        {discountCodes.length > 0 ? (
                            <div className="table-container">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Partner ID</th>
                                            <th>Reduction %</th>
                                            <th>Partner %</th>
                                            <th>Printable Flyers</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {discountCodes.map(c => (
                                            <tr key={c.id}>
                                                <td>{c.code}</td>
                                                <td>{c.partner_id}</td>
                                                <td>{c.reduction_percent}%</td>
                                                <td>{c.partner_percent}%</td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <a href={`/api/download-flyer?code=${c.code}&format=A4&gameId=${dcGameId || 1}`} target="_blank" rel="noreferrer" style={{ padding: '3px 8px', background: '#4CAF50', color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '11px', fontWeight: 'bold' }}>PDF A4</a>
                                                        <a href={`/api/download-flyer?code=${c.code}&format=A5&gameId=${dcGameId || 1}`} target="_blank" rel="noreferrer" style={{ padding: '3px 8px', background: '#2196F3', color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '11px', fontWeight: 'bold' }}>PDF A5</a>
                                                        <a href={`/api/download-flyer?code=${c.code}&format=A6&gameId=${dcGameId || 1}`} target="_blank" rel="noreferrer" style={{ padding: '3px 8px', background: '#9C27B0', color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '11px', fontWeight: 'bold' }}>PDF A6</a>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : <p>No discount codes found.</p>}
                    </div>
                )}

                {/* Surveys List Section */}
                {activeTab === 'surveys' && (
                    <div className="admin-section">
                        <h2 className="section-title">{t('all_surveys', { defaultValue: 'All Submitted Surveys' })}</h2>
                        {isLoading && <p>{t('loading', { defaultValue: 'Loading' })}...</p>}
                        {surveys.length > 0 ? (
                            <div className="table-container">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t('survey_id', { defaultValue: 'ID' })}</th>
                                            <th>{t('game_title', { defaultValue: 'Game' })}</th>
                                            <th>{t('submitted_by', { defaultValue: 'Submitted By' })}</th>
                                            <th>{t('experience_rating', { defaultValue: 'Experience' })}</th>
                                            <th>{t('difficulty_rating', { defaultValue: 'Difficulty' })}</th>
                                            <th>{t('had_problem', { defaultValue: 'Had Problem' })}</th>
                                            <th>{t('problem_details', { defaultValue: 'Problem Details' })}</th>
                                            <th>{t('map_recommendations', { defaultValue: 'Map Recs' })}</th>
                                            <th>{t('general_feedback', { defaultValue: 'General Feedback' })}</th>
                                            <th>{t('learned_about', { defaultValue: 'Learned About' })}</th>
                                            <th>{t('most_least_liked_riddle', { defaultValue: 'Most/Least Liked Riddle' })}</th>
                                            <th>{t('submitted_at', { defaultValue: 'Submitted At' })}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {surveys.map((survey) => (
                                            <tr key={survey.survey_id}>
                                                <td>{survey.survey_id}</td>
                                                <td>{survey.game_title}</td>
                                                <td>{survey.submitted_by_username || survey.submitted_by_email || 'Guest'}</td>
                                                <td>{survey.experienceRating}</td>
                                                <td>{t(survey.difficultyRating, { defaultValue: survey.difficultyRating })}</td>
                                                <td>{survey.hadProblem ? t('Yes', { defaultValue: 'Yes' }) : t('No', { defaultValue: 'No' })}</td>
                                                <td>{survey.problemDetails || '-'}</td>
                                                <td>{t(survey.mapRecommendations, { defaultValue: survey.mapRecommendations }) || '-'}</td>
                                                <td>{survey.feedbackComment || '-'}</td>
                                                <td>{t(survey.learnedAbout, { defaultValue: survey.learnedAbout }) || survey.learnedAboutOther || '-'}</td>
                                                <td>{survey.likedLeastMostRiddle || '-'}</td>
                                                <td>{new Date(survey.submitted_at).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {hasMoreSurveys && (
                                    <button
                                        onClick={handleLoadMoreSurveys}
                                        disabled={isLoading}
                                        className="submit-button"
                                        style={{ marginTop: '10px' }}
                                    >
                                        {t('load_more', { defaultValue: 'Load More' })}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <p>{t('no_surveys_found', { defaultValue: 'No surveys have been submitted yet.' })}</p>
                        )}
                    </div>
                )}

                {/* Play IDs History Section */}
                {activeTab === 'playHistory' && (
                    <div className="admin-section">
                        <h2 className="section-title">{t('admin_play_history', { defaultValue: 'Play History' })}</h2>
                        {playsHistory.length > 0 ? (
                            <div className="table-container">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>{t('game_title', { defaultValue: 'Game' })}</th>
                                            <th>{t('user_started', { defaultValue: 'User Started' })}</th>
                                            <th>{t('date_started', { defaultValue: 'Date Started' })}</th>
                                            <th>{t('game_status', { defaultValue: 'Status' })}</th>
                                            <th>{t('last_step', { defaultValue: 'Last Step' })}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {playsHistory.map((play) => (
                                            <tr
                                                key={play.play_id}
                                                onClick={() => navigate('/game-overview', { state: { game: play.game_id, play_id: play.play_id, isAdmin: true } })}
                                                style={{ cursor: 'pointer' }}
                                                className="clickable-row"
                                            >
                                                <td>{play.play_id}</td>
                                                <td>{play.game_title}</td>
                                                <td>{play.user_started || '-'}</td>
                                                <td>{new Date(play.start_date).toLocaleString()}</td>
                                                <td>{play.game_status ? t('completed', { defaultValue: 'Completed' }) : t('not_completed', { defaultValue: 'Not Completed' })}</td>
                                                <td>{play.last_step}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {hasMorePlays && (
                                    <button
                                        onClick={handleLoadMorePlays}
                                        disabled={isLoading}
                                        className="submit-button"
                                        style={{ marginTop: '10px' }}
                                    >
                                        {t('load_more', { defaultValue: 'Load More' })}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <p>{t('no_data_for_table', { defaultValue: 'No data available for this table.' })}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthContainer;